import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApi, postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';

const formatUsd = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const CallCredit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credit, setCredit] = useState(null);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [buyingPlanId, setBuyingPlanId] = useState(null);

  useEffect(() => {
    fetchCallCredit();
    fetchTopUpPlans();
  }, []);

  const fetchCallCredit = async () => {
    setLoading(true);
    try {
      const response = await getApi('/billing/call-credit');

      if (response?.statusCode === 200) {
        setCredit(response?.data || null);
      } else {
        setCredit(null);
        toast.error(response?.message || 'Failed to load call credit');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to load call credit';
      setCredit(null);
      toast.error(errorMessage);
      console.error('Error fetching call credit:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopUpPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await getApi('/billing/call-credit/top-ups');

      if (response?.statusCode === 200) {
        setPlans(Array.isArray(response?.data) ? response.data : []);
      } else {
        setPlans([]);
        toast.error(response?.message || 'Failed to load top-up plans');
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to load top-up plans';
      setPlans([]);
      toast.error(errorMessage);
      console.error('Error fetching top-up plans:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleBuy = async (plan) => {
    setBuyingPlanId(plan.id);
    try {
      const response = await postApi('/billing/call-credit/top-up', { plan_id: plan.id });
      const data = response?.data || {};
      const clientSecret = data.client_secret || data.clientSecret || '';

      if (!clientSecret) {
        toast.error(response?.message || 'Failed to start the top-up');
        return;
      }

      navigate(`/landlord/settings/call-credit/top-up/${plan.id}`, {
        state: { clientSecret, plan: { ...plan, ...data.plan } },
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to start the top-up';
      toast.error(errorMessage);
      console.error('Error starting call credit top-up:', error);
    } finally {
      setBuyingPlanId(null);
    }
  };

  return (
    <div className="billing-usage-page">
      <div className="billing-usage-container">
        <div className="billing-usage-card">
          <div className="billing-usage-header">
            <div>
              <h2 className="billing-usage-title">Call Credit</h2>
              <p className="billing-usage-subtitle">
                Each tenant-screening call is deducted from this prepaid balance. With too little
                credit, the call is not placed.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="billing-usage-empty">Loading call credit...</div>
          ) : !credit ? (
            <div className="billing-usage-empty">Call credit is not available.</div>
          ) : (
            <>
              <div className="call-credit-hero">
                <div className="call-credit-hero-main">
                  <span className="call-credit-hero-label">Available balance</span>
                  <span className="call-credit-hero-balance">{formatUsd(credit.balance_usd)}</span>
                </div>
                <div className="call-credit-hero-stats">
                  <div className="call-credit-hero-stat">
                    <span className="call-credit-hero-stat-value">{credit.calls_remaining ?? 0}</span>
                    <span className="call-credit-hero-stat-label">Calls remaining</span>
                  </div>
                  <div className="call-credit-hero-divider" />
                  <div className="call-credit-hero-stat">
                    <span className="call-credit-hero-stat-value">{formatUsd(credit.call_price_usd)}</span>
                    <span className="call-credit-hero-stat-label">Price per call</span>
                  </div>
                </div>
              </div>

              {Number(credit.calls_remaining || 0) === 0 && (
                <div className="call-credit-alert" role="alert">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span>
                    Your balance is too low to place a screening call. Buy a top-up below to continue.
                  </span>
                </div>
              )}
            </>
          )}

          <div className="call-credit-section-header">
            <h3 className="call-credit-section-title">Top-up Plans</h3>
            <p className="call-credit-section-subtitle">
              Pay once and the credit is added to your balance.
            </p>
          </div>

          {plansLoading ? (
            <div className="billing-usage-empty">Loading top-up plans...</div>
          ) : plans.length === 0 ? (
            <div className="billing-usage-empty">No top-up plans available.</div>
          ) : (
            <div className="call-credit-plans">
              {plans.map((plan) => {
                const calls = Number(plan.calls || 0);
                const perCall = calls > 0 ? Number(plan.price_usd || 0) / calls : null;

                return (
                  <div className="call-credit-plan" key={plan.id}>
                    <span className="call-credit-plan-name">{plan.name}</span>
                    <span className="call-credit-plan-price">{formatUsd(plan.price_usd)}</span>
                    <ul className="call-credit-plan-features">
                      <li>
                        {calls} screening {calls === 1 ? 'call' : 'calls'}
                      </li>
                      {perCall !== null && <li>{formatUsd(perCall)} per call</li>}
                      <li>One-time payment</li>
                    </ul>
                    <button
                      type="button"
                      className="call-credit-buy-button"
                      onClick={() => handleBuy(plan)}
                      disabled={buyingPlanId !== null}
                    >
                      {buyingPlanId === plan.id ? 'Please wait...' : `Buy for ${formatUsd(plan.price_usd)}`}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallCredit;
